import React, { createContext, useContext } from 'react';

export const PermissionContext = createContext({ menus: [], btns: [], resources: [] });
export function hasPermission(permission, key) { return Boolean(key && (permission?.all === true || (permission?.btns || []).includes(key) || (permission?.menu && (permission?.btns || []).includes(`${permission.menu}:${key}`)))); }
export function usePermissions() {
    const permission = useContext(PermissionContext);
    return { permission, can: (key) => hasPermission(permission, key) };
}
export const PermissionControl = React.forwardRef(function PermissionControl({ permission: key, variant, targetMenu, actionOnly = false, children, fallback = null, ...controlProps }, ref) {
    const { permission, can } = usePermissions();
    const allowed = can(variant ? `${key}.${variant}` : key) && (!targetMenu || permission.all === true || permission.menus?.includes(targetMenu));
    if (!allowed) {
        if (actionOnly && React.isValidElement(children)) {
            if (children.props.editable !== undefined) return React.cloneElement(children, { editable: false });
            if (['a', 'span', 'div', 'img'].includes(children.type)) return React.cloneElement(children, {
                onClick: undefined, onKeyDown: undefined, href: undefined, target: undefined, role: undefined, tabIndex: undefined,
                style: { ...children.props.style, cursor: 'default', textDecoration: 'none', color: 'inherit' },
            });
            if (children.props.onClose && !children.props.onOk) return React.cloneElement(children, { closable: false, onClose: undefined });
            return React.cloneElement(children, { onOk: undefined, okButtonProps: { ...children.props.okButtonProps, disabled: true } });
        }
        return fallback;
    }
    // Form.Item injects value, checked, onChange and refs into its direct child.
    // Preserve that contract when an authorized control is wrapped here.
    if (React.isValidElement(children) && (Object.keys(controlProps).length || ref)) {
        const forwarded = { ...controlProps };
        Object.entries(controlProps).forEach(([name, value]) => {
            if (/^on[A-Z]/.test(name) && typeof value === 'function' && typeof children.props[name] === 'function' && value !== children.props[name]) {
                const original = children.props[name];
                forwarded[name] = (...args) => { value(...args); original(...args); };
            }
        });
        if (ref) forwarded.ref = ref;
        children = React.cloneElement(children, forwarded);
    }
    if (React.isValidElement(children) && ['a', 'div', 'span', 'img'].includes(children.type) && children.props.onClick && !children.props.href) {
        return React.cloneElement(children, {
            role: children.props.role || 'button',
            tabIndex: children.props.tabIndex ?? 0,
            onKeyDown: event => {
                children.props.onKeyDown?.(event);
                if (!event.defaultPrevented && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); event.currentTarget.click(); }
            },
        });
    }
    return children;
});
export function permissionChanged() { window.dispatchEvent(new Event('admin-permissions-changed')); }
